import type {
  MarkerInput,
  TranscriptSegment,
  VideoIndexedMediaResponse,
  VideoMarker,
} from "@friends-church-dvr/contracts";
import { markerReviewConfidenceThreshold } from "@friends-church-dvr/contracts";
import {
  formatMediaTimestamp,
  mediaTimestampFromParts,
  mediaTimestampFromOptionalParts,
  mediaTimestampParts,
  parseMediaTimestamp,
} from "./indexed-media-timestamp";
import { responseErrorMessage } from "./request-error";
import { groupTranscriptByPlan, type GroupedTranscriptItem } from "./transcript-grouping";

interface IndexedMediaOptions {
  markerContainer: HTMLElement;
  markerEditorContainer: HTMLElement;
  transcriptContainer: HTMLElement;
  videoId: string;
  canEdit: boolean;
  seek: (startMs: number) => void;
  currentPosition: () => Promise<number>;
  timestampSelected?: () => void;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  })[character] ?? character);

const markerFields = (marker: VideoMarker | null): string => {
  const isPlanningCenterMarker = marker?.markerType === "plan_item" && Boolean(marker.pcoPlanItemId);
  const start = mediaTimestampParts(marker?.startMs ?? 0);
  const end = marker?.endMs === null || marker?.endMs === undefined
    ? null
    : mediaTimestampParts(marker.endMs);
  return `
    <input name="markerType" type="hidden" value="${isPlanningCenterMarker ? "plan_item" : "manual"}">
    <input name="pcoPlanItemId" type="hidden" value="${isPlanningCenterMarker ? escapeHtml(marker?.pcoPlanItemId ?? "") : ""}">
    <div class="config-grid two-column indexed-media-fields">
      <div class="marker-identity-fields">
        <label class="config-field"><span>Label</span><input name="label" maxlength="200" required value="${escapeHtml(marker?.label ?? "")}"></label>
        <label class="config-toggle compact-toggle"><input name="isConfirmed" type="checkbox" ${marker?.isConfirmed ?? true ? "checked" : ""}><span><strong>Confirmed</strong><small>Reviewed by an editor.</small></span></label>
      </div>
      <fieldset class="marker-time-field">
        <legend>Start timestamp</legend>
        <div class="marker-time-inputs">
          <label class="config-field"><span>Hours</span><input name="startHours" type="number" min="0" max="24" step="1" inputmode="numeric" required value="${start.hours}"></label>
          <label class="config-field"><span>Minutes</span><input name="startMinutes" type="number" min="0" max="59" step="1" inputmode="numeric" required value="${start.minutes}"></label>
          <label class="config-field"><span>Seconds</span><input name="startSeconds" type="number" min="0" max="59" step="1" inputmode="numeric" required value="${start.seconds}"></label>
        </div>
        <button class="secondary-button compact use-current-position" data-position-target="start" type="button">Use Current Video Position</button>
      </fieldset>
      <fieldset class="marker-time-field">
        <legend>End timestamp (optional)</legend>
        <div class="marker-time-inputs">
          <label class="config-field"><span>Hours</span><input name="endHours" type="number" min="0" max="24" step="1" inputmode="numeric" value="${end?.hours ?? ""}"></label>
          <label class="config-field"><span>Minutes</span><input name="endMinutes" type="number" min="0" max="59" step="1" inputmode="numeric" value="${end?.minutes ?? ""}"></label>
          <label class="config-field"><span>Seconds</span><input name="endSeconds" type="number" min="0" max="59" step="1" inputmode="numeric" value="${end?.seconds ?? ""}"></label>
        </div>
        <button class="secondary-button compact use-current-position" data-position-target="end" type="button">Use Current Video Position as End</button>
      </fieldset>
    </div>`;
};

const markerOriginIcon = (marker: VideoMarker): string => {
  const needsReview = marker.source === "pco_alignment" && !marker.isConfirmed &&
    (marker.confidence ?? 0) < markerReviewConfidenceThreshold;
  const automaticallyVerified = marker.source === "pco_alignment" && marker.isAutoVerified;
  return marker.source === "pco_alignment" ? `
  <span class="marker-origin marker-origin-pco${needsReview ? " marker-origin-review" : ""}" title="${needsReview ? "Low-confidence Planning Center Marker; review placement" : automaticallyVerified ? "Automatically verified from independent transcript evidence; still eligible for review" : "Synced from Planning Center"}" aria-label="${needsReview ? "Planning Center Marker needs placement review" : automaticallyVerified ? "Planning Center Marker automatically verified" : "Synced from Planning Center"}">PCO${needsReview ? '<span class="marker-review-badge" aria-hidden="true">!</span>' : automaticallyVerified ? '<span class="marker-review-badge" aria-hidden="true">✓</span>' : ""}</span>` : `
  <span class="marker-origin marker-origin-manual" title="Manual Marker" aria-label="Manual Marker">
    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m19.4 13.5 1.4 1.1-2 3.5-1.7-.7a7.7 7.7 0 0 1-2.2 1.3L14.7 21h-4l-.3-2.2a7.7 7.7 0 0 1-2.2-1.3l-1.7.7-2-3.5 1.4-1.1a7.8 7.8 0 0 1 0-2.6l-1.4-1.1 2-3.5 1.7.7a7.7 7.7 0 0 1 2.2-1.3l.3-2.2h4l.3 2.2a7.7 7.7 0 0 1 2.2 1.3l1.7-.7 2 3.5-1.4 1.1a7.8 7.8 0 0 1 0 2.6ZM12.7 15a3 3 0 1 0-1.4-5.8A3 3 0 0 0 12.7 15Z"/></svg>
  </span>`;
};

const markerMarkup = (marker: VideoMarker, canEdit: boolean): string => `
  <li class="indexed-marker" data-marker-id="${escapeHtml(marker.id)}">
    <button class="timestamp-button" type="button" data-seek-ms="${marker.startMs}" aria-label="Play ${escapeHtml(marker.label)} from ${formatMediaTimestamp(marker.startMs)}">${formatMediaTimestamp(marker.startMs)}</button>
    ${markerOriginIcon(marker)}
    <span class="indexed-marker-name">${escapeHtml(marker.label)}</span>
    ${canEdit ? `<button class="marker-edit-trigger" type="button" data-edit-marker-id="${escapeHtml(marker.id)}" aria-label="Edit ${escapeHtml(marker.label)}">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l11-11-4-4L4 16v4Zm12.2-16.2 4 4 1.1-1.1a1.4 1.4 0 0 0 0-2l-2-2a1.4 1.4 0 0 0-2 0l-1.1 1.1Z"/></svg>
    </button>` : ""}
  </li>`;

const markerEditorMarkup = (marker: VideoMarker | null): string => `
  <details class="marker-edit-panel" open>
    <summary><span><strong>${marker ? "Edit Marker" : "Add Marker"}</strong><small>${marker ? escapeHtml(marker.label) : "Create a timestamp at the current video position."}</small></span><span aria-hidden="true">+</span></summary>
    <form class="marker-form" ${marker ? `data-marker-id="${escapeHtml(marker.id)}"` : ""}>
      ${markerFields(marker)}
      <p class="form-message" role="status" aria-live="polite"></p>
      <div class="form-actions"><button class="primary-button compact" type="submit">${marker ? "Save Marker" : "Add Marker"}</button>${marker ? '<button class="danger-button marker-delete" type="button">Delete Marker</button>' : ""}</div>
    </form>
  </details>`;

const segmentMarkup = (segment: TranscriptSegment, canEdit: boolean): string => `
  <li class="transcript-segment" data-segment-id="${escapeHtml(segment.id)}">
    <button class="timestamp-button" type="button" data-seek-ms="${segment.startMs}">${formatMediaTimestamp(segment.startMs)}</button>
    <div>
      <p>${escapeHtml(segment.text)}</p>
      ${canEdit ? `<details class="indexed-editor"><summary>Correct segment</summary><form class="segment-form" data-segment-id="${escapeHtml(segment.id)}">
        <div class="config-grid two-column indexed-media-fields">
          <label class="config-field"><span>Start timestamp</span><input name="startTime" required value="${formatMediaTimestamp(segment.startMs)}"></label>
          <label class="config-field"><span>End timestamp</span><input name="endTime" required value="${formatMediaTimestamp(segment.endMs)}"></label>
        </div>
        <label class="config-field"><span>Transcript text</span><textarea name="text" rows="3" maxlength="4000" required>${escapeHtml(segment.text)}</textarea></label>
        <p class="form-message" role="status" aria-live="polite"></p>
        <button class="primary-button compact" type="submit">Save correction</button>
      </form></details>` : ""}
    </div>
  </li>`;

const transcriptConfidenceMarkup = (confidence: number | null): string => {
  if (confidence === null) return '<span class="transcript-confidence unscored" title="A confidence score was not captured when this transcript was generated.">Not scored</span>';
  const percentage = Math.round(Math.min(1, Math.max(0, confidence)) * 100);
  const level = percentage >= 85 ? "high" : percentage >= 65 ? "medium" : "low";
  return `<span class="transcript-confidence ${level}" title="Automated transcript confidence; this is not a human review.">Confidence ${percentage}%</span>`;
};

const clipboardIcon = (): string => '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="11" height="12" rx="1.5"></rect><path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v10A1.5 1.5 0 0 0 5.5 17H8"></path></svg>';
const copiedIcon = (): string => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4.2 4.2L19 6.5"></path></svg>';
const emailIcon = (): string => '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"></rect><path d="m4 7 8 6 8-6"></path></svg>';
const emailFailedIcon = (): string => '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 7 10 10M17 7 7 17"></path></svg>';

const groupedTranscriptItemMarkup = (item: GroupedTranscriptItem, canEdit: boolean): string => `
  <details class="transcript-plan-item">
    <summary class="transcript-plan-item-heading">
      <button class="timestamp-button" type="button" data-seek-ms="${item.startMs}">${formatMediaTimestamp(item.startMs)}</button>
      <h4>${escapeHtml(item.title)}</h4>
      <span class="transcript-section-actions">
        <button class="transcript-section-copy" type="button" title="Copy ${escapeHtml(item.title)} transcript" aria-label="Copy ${escapeHtml(item.title)} transcript">${clipboardIcon()}</button>
        <button class="transcript-section-email" type="button" data-transcript-title="${escapeHtml(item.title)}" data-transcript-segment-ids="${escapeHtml(item.sourceSegments.map((segment) => segment.id).join(","))}" title="Email ${escapeHtml(item.title)} transcript to me" aria-label="Email ${escapeHtml(item.title)} transcript to me">${emailIcon()}</button>
      </span>
      ${transcriptConfidenceMarkup(item.confidence)}
      <span class="transcript-plan-item-toggle" aria-hidden="true">+</span>
    </summary>
    <div class="transcript-plan-item-content">
      <p class="transcript-plan-copy">${escapeHtml(item.segments.map((segment) => segment.text).join(" "))}</p>
      ${canEdit && item.sourceSegments.length ? `<details class="transcript-timed-editor">
        <summary>Correct timed text <span>${item.sourceSegments.length} source ${item.sourceSegments.length === 1 ? "segment" : "segments"}</span></summary>
        <ol class="transcript-list">${item.sourceSegments.map((segment) => segmentMarkup(segment, true)).join("")}</ol>
      </details>` : ""}
    </div>
  </details>`;

const groupedTranscriptMarkup = (payload: VideoIndexedMediaResponse, canEdit: boolean): string => {
  const items = groupTranscriptByPlan(payload).flatMap((group) => group.items);
  if (!items.length) return "";
  return `<div class="transcript-plan-items">${items.map((item) => groupedTranscriptItemMarkup(item, canEdit)).join("")}</div>`;
};

const transcriptItemCount = (payload: VideoIndexedMediaResponse): number =>
  groupTranscriptByPlan(payload).reduce((total, group) => total + group.items.length, 0);

const transcriptStatusCopy = (payload: VideoIndexedMediaResponse): string => {
  if (!payload.transcript) return "A transcript has not been generated for this Video yet.";
  const progress = payload.transcript.processedMs === null ? "" : ` ${formatMediaTimestamp(payload.transcript.processedMs)} processed.`;
  if (payload.transcript.status === "queued") return `Transcription is queued.${progress}`;
  if (payload.transcript.status === "processing") return `Transcription is in progress.${progress}`;
  if (payload.transcript.status === "failed") return "Transcription needs operational attention before text is available.";
  return payload.transcript.segments.length ? "" : "The transcript is ready but contains no timed segments.";
};

const markerSectionMarkup = (payload: VideoIndexedMediaResponse, canEdit: boolean): string => `
  <section class="marker-panel" aria-label="Video Markers">
    ${payload.markers.length ? `<ol class="marker-list">${payload.markers.map((marker) => markerMarkup(marker, canEdit)).join("")}</ol>` : '<p class="indexed-empty">No Markers have been added.</p>'}
    ${canEdit ? '<button class="marker-create-trigger" type="button">Add Marker</button>' : ""}
  </section>`;

const transcriptSectionMarkup = (payload: VideoIndexedMediaResponse, canEdit: boolean): string => `
  <details class="event-details-section transcript-details-section" aria-labelledby="transcript-section-title">
    <summary><span><span class="transcript-title-row"><strong id="transcript-section-title">Transcript</strong>${payload.transcript?.segments.length ? `<span class="transcript-summary-actions"><button class="transcript-section-copy transcript-all-copy" type="button" title="Copy entire transcript" aria-label="Copy entire transcript">${clipboardIcon()}</button><button class="transcript-section-email transcript-all-email" type="button" data-transcript-title="Entire transcript" title="Email entire transcript to me" aria-label="Email entire transcript to me">${emailIcon()}</button></span>` : ""}</span><small>${payload.transcript
      ? `${escapeHtml(payload.transcript.language.toUpperCase())} · ${escapeHtml(payload.transcript.status)} · ${transcriptItemCount(payload)} service ${transcriptItemCount(payload) === 1 ? "item" : "items"}`
      : "Grouped by Planning Center service plan."}</small></span><span aria-hidden="true">+</span></summary>
    <div class="event-details-content transcript-details-content">
      <div class="transcript-panel">
        ${transcriptStatusCopy(payload) ? `<p class="indexed-empty">${escapeHtml(transcriptStatusCopy(payload))}</p>` : ""}
        ${groupedTranscriptMarkup(payload, canEdit)}
        ${canEdit && payload.transcript ? `<details class="event-detail-subsection transcript-options">
          <summary><span><strong>Transcript Options</strong></span><span aria-hidden="true">+</span></summary>
          <p class="transcript-options-copy">For minor transcript corrections, expand the section above and make the correction. If there is a major section missing or the transcript is completely inaccurate, Re-process Transcription may be necessary. Keep in mind, this will delete the current copy and re-generate from the original video. It could take up to an hour to complete. You can also choose to Delete Transcript; however, this will not re-process a new version of the transcript.</p>
          <div class="transcript-actions"><button class="secondary-button compact transcript-restart" type="button">Re-process Transcription</button><button class="danger-button compact transcript-delete" type="button">Delete Transcript</button></div>
        </details>` : ""}
      </div>
    </div>
  </details>`;

const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const fallback = document.createElement("textarea");
  fallback.value = text;
  fallback.setAttribute("readonly", "");
  fallback.style.position = "fixed";
  fallback.style.opacity = "0";
  document.body.append(fallback);
  fallback.select();
  const copied = document.execCommand("copy");
  fallback.remove();
  if (!copied) throw new Error("clipboard_unavailable");
};

const markerInput = (form: HTMLFormElement): MarkerInput => {
  const data = new FormData(form);
  const markerType = String(data.get("markerType")) as MarkerInput["markerType"];
  const planItem = String(data.get("pcoPlanItemId") ?? "").trim();
  return {
    markerType,
    pcoPlanItemId: markerType === "plan_item" && planItem ? planItem : null,
    label: String(data.get("label") ?? "").trim(),
    startMs: mediaTimestampFromParts(
      Number(data.get("startHours")),
      Number(data.get("startMinutes")),
      Number(data.get("startSeconds")),
    ),
    endMs: mediaTimestampFromOptionalParts(
      String(data.get("endHours") ?? ""),
      String(data.get("endMinutes") ?? ""),
      String(data.get("endSeconds") ?? ""),
    ),
    isConfirmed: data.get("isConfirmed") === "on",
  };
};

const writeJson = async (url: string, method: "POST" | "PATCH" | "DELETE", body?: unknown): Promise<void> => {
  const response = await fetch(url, {
    method,
    headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await responseErrorMessage(response));
};

const setFormMessage = (form: HTMLFormElement, value: string, error = false): void => {
  const message = form.querySelector<HTMLElement>(".form-message");
  if (message) {
    message.textContent = value;
    message.className = error ? "form-message error-message" : "form-message";
  }
};

export const renderIndexedMedia = async (options: IndexedMediaOptions): Promise<void> => {
  let selectedMarkerId: string | null = null;
  let creatingMarker = false;

  const wireCurrentPosition = (form: HTMLFormElement): void => {
    form.querySelectorAll<HTMLButtonElement>(".use-current-position").forEach((button) => button.addEventListener("click", () => {
      void (async () => {
        try {
          button.disabled = true;
          setFormMessage(form, "Reading current video position…");
          const positionMs = await options.currentPosition();
          const position = mediaTimestampParts(positionMs);
          const target = button.dataset.positionTarget === "end" ? "end" : "start";
          (form.elements.namedItem(`${target}Hours`) as HTMLInputElement).value = String(position.hours);
          (form.elements.namedItem(`${target}Minutes`) as HTMLInputElement).value = String(position.minutes);
          (form.elements.namedItem(`${target}Seconds`) as HTMLInputElement).value = String(position.seconds);
          setFormMessage(form, `${target === "end" ? "End" : "Start"} timestamp set to ${formatMediaTimestamp(positionMs)}.`);
          (form.elements.namedItem(`${target}Seconds`) as HTMLInputElement).focus();
        } catch (error) {
          setFormMessage(form, error instanceof Error ? error.message : "The current video position could not be read.", true);
        } finally {
          button.disabled = false;
        }
      })();
    }));
  };

  const wireMarkerForm = (form: HTMLFormElement): void => {
    wireCurrentPosition(form);
    form.addEventListener("submit", (event) => {
      event.preventDefault(); void (async () => {
        try {
          setFormMessage(form, "Saving…");
          const markerId = form.dataset.markerId;
          await writeJson(
            markerId
              ? `/api/videos/${encodeURIComponent(options.videoId)}/markers/${encodeURIComponent(markerId)}`
              : `/api/videos/${encodeURIComponent(options.videoId)}/markers`,
            markerId ? "PATCH" : "POST",
            markerInput(form),
          );
          selectedMarkerId = null;
          creatingMarker = false;
          await load();
        } catch (error) {
          setFormMessage(form, error instanceof Error ? error.message : "The Marker could not be saved.", true);
        }
      })();
    });
    form.querySelector<HTMLButtonElement>(".marker-delete")?.addEventListener("click", () => {
      void (async () => {
        const markerId = form.dataset.markerId;
        if (!markerId || !window.confirm("Delete this Marker? The action will be recorded in the audit log.")) return;
        try {
          setFormMessage(form, "Deleting…");
          await writeJson(`/api/videos/${encodeURIComponent(options.videoId)}/markers/${encodeURIComponent(markerId)}`, "DELETE");
          selectedMarkerId = null;
          await load();
        } catch (error) {
          setFormMessage(form, error instanceof Error ? error.message : "The Marker could not be deleted.", true);
        }
      })();
    });
  };

  const showMarkerEditor = (marker: VideoMarker | null, scroll = true): void => {
    selectedMarkerId = marker?.id ?? null;
    creatingMarker = marker === null;
    options.markerEditorContainer.innerHTML = markerEditorMarkup(marker);
    const form = options.markerEditorContainer.querySelector<HTMLFormElement>(".marker-form");
    if (form) wireMarkerForm(form);
    if (scroll) {
      options.markerEditorContainer.querySelector<HTMLElement>(".marker-edit-panel")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.requestAnimationFrame(() => form?.querySelector<HTMLInputElement>('input[name="label"]')?.focus({ preventScroll: true }));
    }
  };

  async function load(): Promise<void> {
    options.markerContainer.innerHTML = '<section class="marker-panel"><p>Loading Markers…</p></section>';
    options.transcriptContainer.innerHTML = '<details class="event-details-section transcript-details-section"><summary><span><strong>Transcript</strong><small>Loading transcript…</small></span><span aria-hidden="true">+</span></summary></details>';
    try {
      const response = await fetch(`/api/videos/${encodeURIComponent(options.videoId)}/indexed-media`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(await responseErrorMessage(response));
      const payload = await response.json() as VideoIndexedMediaResponse;
      options.markerContainer.innerHTML = markerSectionMarkup(payload, options.canEdit);
      options.transcriptContainer.innerHTML = transcriptSectionMarkup(payload, options.canEdit);
      [options.markerContainer, options.transcriptContainer].forEach((container) => {
        container.querySelectorAll<HTMLButtonElement>("[data-seek-ms]").forEach((button) => {
          button.addEventListener("click", (clickEvent) => {
            const markerSelection = container === options.markerContainer;
            if (!markerSelection) {
              clickEvent.preventDefault();
              clickEvent.stopPropagation();
            }
            options.seek(Number(button.dataset.seekMs));
            options.timestampSelected?.();
          });
        });
      });
      options.transcriptContainer.querySelectorAll<HTMLButtonElement>(".transcript-section-copy").forEach((button) => {
        button.addEventListener("click", (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          const section = button.closest<HTMLElement>(".transcript-plan-item");
          const title = section?.querySelector("h4")?.textContent?.trim() || "entire";
          const text = section?.querySelector(".transcript-plan-copy")?.textContent?.trim()
            ?? payload.transcript?.segments.map((segment) => segment.text).join(" ").trim();
          if (!text) return;
          void copyToClipboard(text).then(() => {
            button.innerHTML = copiedIcon();
            button.title = "Copied";
            button.setAttribute("aria-label", `Copied ${title} transcript`);
            window.setTimeout(() => {
              button.innerHTML = clipboardIcon();
              button.title = `Copy ${title} transcript`;
              button.setAttribute("aria-label", `Copy ${title} transcript`);
            }, 2_000);
          }).catch(() => {
            button.title = "Unable to copy";
          });
        });
      });
      options.transcriptContainer.querySelectorAll<HTMLButtonElement>(".transcript-section-email").forEach((button) => {
        button.addEventListener("click", (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          const sectionTitle = button.dataset.transcriptTitle ?? "Transcript";
          const segmentIds = button.dataset.transcriptSegmentIds?.split(",").filter(Boolean) ?? null;
          const sections = button.classList.contains("transcript-all-email")
            ? groupTranscriptByPlan(payload).flatMap((group) => group.items)
              .map((item) => ({ title: item.title, segmentIds: item.sourceSegments.map((segment) => segment.id) }))
              .filter((section) => section.segmentIds.length > 0)
            : undefined;
          void (async () => {
            button.disabled = true;
            try {
              const response = await fetch(`/api/videos/${encodeURIComponent(options.videoId)}/transcript/email`, {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
                body: JSON.stringify({ sectionTitle, segmentIds, sections }),
              });
              if (!response.ok) throw new Error(await responseErrorMessage(response));
              button.classList.remove("transcript-email-failed");
              button.innerHTML = copiedIcon();
              button.title = "Sent to your email";
              button.setAttribute("aria-label", "Transcript email sent to your email");
            } catch (error) {
              const message = error instanceof Error ? error.message : "Transcript email could not be sent";
              button.classList.add("transcript-email-failed");
              button.innerHTML = emailFailedIcon();
              button.title = message;
              button.setAttribute("aria-label", `Transcript email failed: ${message}`);
            } finally {
              button.disabled = false;
            }
          })();
        });
      });
      options.markerContainer.querySelectorAll<HTMLButtonElement>("[data-edit-marker-id]").forEach((button) => {
        button.addEventListener("click", () => {
          const marker = payload.markers.find(({ id }) => id === button.dataset.editMarkerId);
          if (marker) showMarkerEditor(marker);
        });
      });
      options.markerContainer.querySelector<HTMLButtonElement>(".marker-create-trigger")?.addEventListener("click", () => {
        showMarkerEditor(null);
      });
      if (creatingMarker) {
        showMarkerEditor(null, false);
      } else if (selectedMarkerId) {
        const selectedMarker = payload.markers.find(({ id }) => id === selectedMarkerId);
        if (selectedMarker) showMarkerEditor(selectedMarker, false);
        else {
          selectedMarkerId = null;
          options.markerEditorContainer.innerHTML = "";
        }
      } else {
        options.markerEditorContainer.innerHTML = "";
      }
      options.transcriptContainer.querySelectorAll<HTMLFormElement>(".segment-form").forEach((form) => {
        form.addEventListener("submit", (event) => {
          event.preventDefault(); void (async () => {
            const data = new FormData(form);
            const segmentId = form.dataset.segmentId;
            if (!segmentId) return;
            try {
              setFormMessage(form, "Saving…");
              await writeJson(
                `/api/videos/${encodeURIComponent(options.videoId)}/transcript-segments/${encodeURIComponent(segmentId)}`,
                "PATCH",
                {
                  startMs: parseMediaTimestamp(String(data.get("startTime") ?? "")),
                  endMs: parseMediaTimestamp(String(data.get("endTime") ?? "")),
                  text: String(data.get("text") ?? "").trim(),
                },
              );
              await load();
            } catch (error) {
              setFormMessage(form, error instanceof Error ? error.message : "The transcript segment could not be saved.", true);
            }
          })();
        });
      });
      options.transcriptContainer.querySelector<HTMLButtonElement>(".transcript-restart")?.addEventListener("click", () => {
        if (!window.confirm("Re-process transcription? Existing transcript sections will be removed and regenerated.")) return;
        void (async () => { try { await writeJson(`/api/videos/${encodeURIComponent(options.videoId)}/transcript/restart`, "POST", {}); await load(); } catch (error) { window.alert(error instanceof Error ? error.message : "The transcript could not be re-processed."); } })();
      });
      options.transcriptContainer.querySelector<HTMLButtonElement>(".transcript-delete")?.addEventListener("click", () => {
        if (!window.confirm("Delete this transcript and all of its searchable sections? This action is recorded in the audit log.")) return;
        void (async () => { try { await writeJson(`/api/videos/${encodeURIComponent(options.videoId)}/transcript`, "DELETE"); await load(); } catch (error) { window.alert(error instanceof Error ? error.message : "The transcript could not be deleted."); } })();
      });
    } catch (error) {
      const message = escapeHtml(error instanceof Error ? error.message : "Transcription and Markers could not be loaded.");
      options.markerContainer.innerHTML = `<section class="marker-panel"><p class="error-message">${message}</p></section>`;
      options.markerEditorContainer.innerHTML = "";
      options.transcriptContainer.innerHTML = "";
    }
  }
  await load();
};
